import { useState, useRef } from 'react';
import api from '../lib/api';

export default function ImportPage() {
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'done'
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      return setError('Please upload an Excel (.xlsx, .xls) or CSV file.');
    }
    setError('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRows(r.data.rows);
      setTotal(r.data.total);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse file.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const r = await api.post('/import/accounts', { rows });
      setResult(r.data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setRows([]);
    setTotal(0);
    setResult(null);
    setError('');
  };

  return (
    <>
      <div style={{ maxWidth:900, margin:'0 auto' }}>

        {/* Step indicator */}
        <div style={{ display:'flex', gap:0, marginBottom:24 }}>
          {['Upload file', 'Preview data', 'Complete'].map((s, i) => {
            const stepKeys = ['upload','preview','done'];
            const active = stepKeys[i] === step;
            const done = stepKeys.indexOf(step) > i;
            return (
              <div key={s} style={{ display:'flex', alignItems:'center', flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{
                    width:24, height:24, borderRadius:'50%', fontSize:11, fontWeight:600,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    background: done ? '#EAF3DE' : active ? 'var(--accent)' : 'var(--bg-secondary)',
                    color: done ? '#27500A' : active ? '#fff' : 'var(--text-tertiary)',
                    border: `0.5px solid ${done ? '#639922' : active ? 'var(--accent)' : 'var(--border-md)'}`,
                  }}>
                    {done ? '✓' : i + 1}
                  </div>
                  <span style={{ fontSize:13, color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
                                  fontWeight: active ? 500 : 400 }}>{s}</span>
                </div>
                {i < 2 && <div style={{ flex:1, height:1, background:'var(--border)', margin:'0 12px' }} />}
              </div>
            );
          })}
        </div>

        {/* STEP 1 — Upload */}
        {step === 'upload' && (
          <div className="card">
            <div className="section-title" style={{ marginBottom:16 }}>Import accounts from Excel or CSV</div>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current.click()}
              style={{
                border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border-md)'}`,
                borderRadius: 'var(--radius-lg)', padding:'48px 24px', textAlign:'center',
                cursor:'pointer', background: dragOver ? 'var(--info-bg)' : 'var(--bg-secondary)',
                transition:'all .15s'
              }}
            >
              <div style={{ fontSize:32, marginBottom:12 }}>📂</div>
              <div style={{ fontSize:14, fontWeight:500, color:'var(--text-primary)', marginBottom:6 }}>
                Drag and drop your file here
              </div>
              <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16 }}>
                or click to browse
              </div>
              <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>
                Supports .xlsx, .xls, .csv
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
                style={{ display:'none' }} onChange={e => handleFile(e.target.files[0])} />
            </div>

            {loading && (
              <div style={{ marginTop:16, fontSize:13, color:'var(--text-secondary)', textAlign:'center' }}>
                Parsing file…
              </div>
            )}

            {error && <div className="form-error" style={{ marginTop:12 }}>{error}</div>}

            <div style={{ marginTop:20 }}>
              <div className="section-title">Expected column headers</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                {['Account name','Address','City','State','Zip Code','Industry','Website'].map(h => (
                  <span key={h} className="badge badge-gray" style={{ fontSize:12 }}>{h}</span>
                ))}
              </div>
              <div style={{ fontSize:12, color:'var(--text-tertiary)', marginTop:8 }}>
                Column names are flexible — we'll match them automatically.
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — Preview */}
        {step === 'preview' && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
                {total} accounts ready to import
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn" onClick={reset}>← Start over</button>
                <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
                  {importing ? 'Importing…' : `Import ${total} accounts`}
                </button>
              </div>
            </div>

            {error && <div className="form-error" style={{ marginBottom:12 }}>{error}</div>}

            <div className="card" style={{ padding:0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Account name</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Zip</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ color:'var(--text-tertiary)', fontSize:12 }}>{i + 1}</td>
                      <td style={{ fontWeight:500 }}>{r.name}</td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{r.address_line1 || '—'}</td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{r.city || '—'}</td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{r.state || '—'}</td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{r.zip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* STEP 3 — Done */}
        {step === 'done' && result && (
          <div className="card" style={{ textAlign:'center', padding:40 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
            <div style={{ fontSize:20, fontWeight:600, marginBottom:8 }}>Import complete</div>
            <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:24 }}>
              Your accounts have been imported successfully.
            </div>
            <div style={{ display:'inline-grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:28 }}>
              <div className="stat-card">
                <div className="stat-label">Imported</div>
                <div className="stat-value" style={{ color:'var(--success)' }}>{result.imported}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Skipped (duplicates)</div>
                <div className="stat-value" style={{ color:'var(--text-secondary)' }}>{result.skipped}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Errors</div>
                <div className="stat-value" style={{ color: result.errors?.length ? 'var(--danger)' : 'var(--text-secondary)' }}>
                  {result.errors?.length || 0}
                </div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div style={{ marginBottom:20, textAlign:'left' }}>
                <div className="section-title">Errors</div>
                {result.errors.map((e, i) => (
                  <div key={i} style={{ fontSize:12, color:'var(--danger)', padding:'4px 0' }}>
                    {e.name}: {e.error}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
              <button className="btn btn-primary" onClick={() => window.location.href = '/accounts'}>
                View accounts
              </button>
              <button className="btn" onClick={reset}>Import another file</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}